/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import {
  Button,
  Checkbox,
  colors,
  CircleThumbnail,
  TableV2,
  TableRow,
  GhostButton
} from '@bonitour/components'
import { identity } from '@bonitour/common-functions'
import { useCallback, useMemo } from 'react'
import { ticketStatusDictionary } from 'constants/ticketStatus'
import { useMediaQuery } from '@bonitour/app-functions'
import { TicketActionsTableCell, TicketStatusTableCell, TicketTableCell, ticketMobileBreakpoint } from 'domains/Ticket/CheckIn/TicketList'
import { Integrations } from 'constants/integrations'

export const paymentPendingThumbnail = css`
  display: inline-flex;
  margin-right: 0.25rem;
  background-color: ${colors.yellow2};
`

export const paymentPendingText = css`
  font-weight: 600;
  color: ${colors.orange6};
`

const columnsWidths = ['3.25rem', '9.875rem', '4.25rem', '12fr', '17fr', '6.75rem', '9rem', '7.25rem']

const MyDayTableRow = ({
  activity = {},
  isMobile = false,
  isTicketSelectable = false,
  onValidateClick: emitValidateClick = identity,
  onTicketClick: emitTicketClick = identity,
  onNavigation: emitMyDayClickRow = identity,
  isTicketSelect: emitTicketSelect = identity,
  canValidateTicket: emitTicketsValidation = identity
}) => {
  const {
    serviceId = '',
    time = '',
    ticketCode = '',
    serviceName = '',
    serviceType = '',
    passengerName = '',
    paxType = '',
    state = '',
    companyName = '',
    commercialPartnerName = '',
    paymentStatus = '',
    externalIntegrations = []
  } = activity

  const limberIntegration = useMemo(
    () => externalIntegrations.find(({ integration_name: name }) => name === Integrations.Limber),
    [externalIntegrations]
  )

  const externalVoucherId = useMemo(
    () => externalIntegrations.map(({ external_ticket_id: id } = {}) => id).find(Boolean),
    [externalIntegrations]
  )

  const [limberQR, pickup] = useMemo(
    () => [
      limberIntegration?.limber_qr_code,
      limberIntegration?.extra_data?.nome_local_embarque
    ],
    [limberIntegration]
  )

  const handleValidateClick = useCallback(
    (event) => {
      event.stopPropagation()
      emitValidateClick({ code: ticketCode })
    },
    [emitValidateClick, ticketCode]
  )

  const handleCheckBoxClick = useCallback(
    (event) => {
      event.stopPropagation()
      emitTicketClick(activity)
    },
    [emitTicketClick, activity]
  )

  const hasPaymentPending = useMemo(() => [ticketStatusDictionary.not_paid.value, ticketStatusDictionary.partially_paid.value].includes(paymentStatus), [paymentStatus])

  const checkbox = useMemo(
    () => (isTicketSelectable(state) && emitTicketsValidation(state, serviceId) && (
      <Checkbox checked={emitTicketSelect(ticketCode)} onClick={(event) => handleCheckBoxClick(event)} />
    )),
    [isTicketSelectable, emitTicketsValidation, state, serviceId, ticketCode, emitTicketSelect, handleCheckBoxClick]
  )

  const {
    isValidatable = false,
    isValidatableByServiceType: validatableTypes
  } = ticketStatusDictionary[state] || {}

  const isValidateVisible = useMemo(
    () => isValidatable || (Array.isArray(validatableTypes) && validatableTypes.includes(serviceType)),
    [isValidatable, validatableTypes, serviceType]
  )

  return (
    <TableRow>
      {[
        ...(isMobile ? [] : [checkbox]),
        <TicketTableCell fontSize='tiny' key='code' className={isMobile && 'cell-with-checkbox'}>
          {isMobile && <span className='mobile-checkbox'>{checkbox}</span>}
          {hasPaymentPending
            ? (
              <>
                <CircleThumbnail size={8} css={paymentPendingThumbnail}/>
                <span css={paymentPendingText}>Pagamento pendente</span>
              </>
            )
            : null
          }
          {ticketCode}
          {externalVoucherId
            ? (
              <>
                <span className='tinier'>Voucher: </span>
                {externalVoucherId}
              </>
            )
            : null}
          {limberQR
            ? (
              <>
                <span className='tinier'>QR: </span>
                {limberQR}
              </>
            )
            : null}
        </TicketTableCell>,
        <TicketTableCell maxLines={3} key='time'>
          <>{time}</>
        </TicketTableCell>,
        <TicketTableCell fontSize='small' maxLines={3} key='client+seller'>
          {passengerName}
          <em>{commercialPartnerName}</em>
        </TicketTableCell>,
        <TicketTableCell maxLines={2} key='company+service'>
          {companyName}
          <strong>{serviceName}</strong>
          {pickup
            ? (
              <>
                <span className='smaller'>Embarque: </span>
                {pickup}
              </>
            )
            : null}
        </TicketTableCell>,
        <TicketTableCell maxLines={3} key='type'>
          {paxType}
        </TicketTableCell>,
        <TicketStatusTableCell status={state} key='status' />,
        <TicketActionsTableCell key='actions'>
          <GhostButton onClick={emitMyDayClickRow}>
            Ver reserva
          </GhostButton>
          {isValidateVisible && emitTicketsValidation(state, serviceId) && (
            <Button onClick={(event) => handleValidateClick(event)}>
              Utilizar
            </Button>
          )}
        </TicketActionsTableCell>
      ]}
    </TableRow>
  )
}

export const MyDayTable = ({
  activities = [],
  isTicketInFuture = false,
  loading = 'false',
  isSelectedAll = false,
  onNavigation: emitNavigationEvent = identity,
  isTicketSelectable = identity,
  onValidateClick = identity,
  onSelectedAllClick: emitSelectedAllClick = identity,
  onTicketClick = identity,
  isTicketSelect = identity,
  canValidateTicket = identity
}) => {
  const isMobile = useMediaQuery(`(max-width: ${ticketMobileBreakpoint}px)`)

  const labels = useMemo(
    () => [
      ...(isMobile ? [] : [<Checkbox key='selectAll' checked={isSelectedAll} onChange={emitSelectedAllClick} />]),
      'Cód. ingresso',
      'Horário',
      'Viajante e vendedor',
      'Serviço',
      'Tipo',
      'Status',
      'Ações'
    ],
    [isSelectedAll, emitSelectedAllClick, isMobile]
  )

  return (
    <>
      <TableV2
        isLoading={loading}
        labels={labels}
        columnsWidths={columnsWidths}
        emptyMessage={'Nenhum ingresso encontrado'}
        customCss={[css`
          padding: 0;
          border: none;

          em {
            display: inline-block;
            font-style: italic;
          }

          strong {
            font-weight: 600;
          }

          .cell-with-checkbox {
            padding-right: 2.5rem;
          }
          .mobile-checkbox{
            display: block;
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
          }
          @media (max-width: 480px) {
            .cell-with-checkbox {
              padding-right: 2.25rem;
            }
            .mobile-checkbox{
              right: 0.25rem;
            }
          }
        `]}
        mobileBreakpoint={ticketMobileBreakpoint}
      >
        {activities
          .filter(activity => activity.status !== 'canceled')
          .map((activity, index) => (
            <MyDayTableRow
              key={index}
              activity={activity}
              onTicketClick={onTicketClick}
              isMobile={isMobile}
              onNavigation={emitNavigationEvent(activity.reservationId)}
              onValidateClick={onValidateClick}
              isTicketInFuture={isTicketInFuture}
              isTicketSelectable={isTicketSelectable}
              isTicketSelect={isTicketSelect}
              canValidateTicket={canValidateTicket}
            />
          ))
        }
      </TableV2>
    </>
  )
}
